import type { FC } from "react";
import { useRef, useState } from "react";

import { useUploadDocument } from "@/api/documents";
import ImagePlus from "@/assets/icons/image-plus";
import Loader from "@/components/ui/loader";
import { cn } from "@/lib/utils";

const acceptedExtensions = ["jpg", "jpeg", "png", "webp", "svg"];

interface EmptyImageCardProps {
  patientId: number;
  isPeriotools: boolean;
}

const EmptyImageCard: FC<EmptyImageCardProps> = ({
  patientId,
  isPeriotools,
}) => {
  const [error, setError] = useState<string | undefined>(undefined);
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadMutation = useUploadDocument();

  const handleFile = async (f: File) => {
    const extension = f.name.includes(".") ? f.name.split(".").pop()! : "";
    if (acceptedExtensions.includes(extension)) {
      setError(undefined);
      setFile(f);
      await handleSubmit(f, f.name.replace(/\.[^/.]+$/, ""));
      setFile(null);
    } else {
      setError(
        "Le fichier doit être au format .jpg, .jpeg, .png, .webp ou .svg"
      );
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      await handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleSubmit = async (submittedFile: File, title: string) => {
    if (!submittedFile || !title) return;
    await uploadMutation.mutateAsync({
      title,
      file: submittedFile,
      patientId,
      isPeriotools,
    });
  };

  return (
    <div className="flex flex-col gap-5 px-6">
      <div
        className={cn(
          "flex flex-col justify-center items-center bg-brand-50 rounded-xl border border-brand-700 border-dashed px-6 py-[57px] w-[305px] text-sm font-semibold text-brand-700 cursor-pointer transition",
          isDragging && "bg-brand-100 border-brand-900"
        )}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setIsDragging(false);
        }}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        {file ? (
          <>
            {uploadMutation.isPending ? <Loader /> : null}
            <span className="text-brand-800">{file.name}</span>
          </>
        ) : (
          <>
            <ImagePlus className="w-12 h-12" />
            <div className="text-sm text-brand-700 font-semibold">
              Ajouter une image
            </div>
          </>
        )}
      </div>

      {error ? <p className="text-error-700">{error}</p> : null}

      <input
        ref={fileInputRef}
        type="file"
        hidden
        onChange={async (e) => {
          if (e.target.files?.[0]) {
            await handleFile(e.target.files[0]);
          }
        }}
        accept=".jpg,.jpeg,.png,.webp,.svg"
      />
    </div>
  );
};

export default EmptyImageCard;
